import { useState, useEffect } from "react";

/**
 * Custom hook to manage visibility based on scroll position.
 * @param {number} threshold - Scroll threshold in pixels.
 * @returns {boolean} - Visibility state.
 */
export const useScrollVisibility = (threshold = 50) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    let frameId = null;
    const hysteresis = 14;
    const showThreshold = Math.max(0, threshold - hysteresis);
    const hideThreshold = threshold + hysteresis;

    const handleScroll = () => {
      if (frameId !== null) return;

      frameId = window.requestAnimationFrame(() => {
        const scrollY = window.scrollY;
        setIsVisible((prev) => {
          const nextVisible = prev
            ? scrollY <= hideThreshold
            : scrollY <= showThreshold;

          return prev === nextVisible ? prev : nextVisible;
        });
        frameId = null;
      });
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [threshold]);

  return isVisible;
};
